/**
 * 웨이터 호출 API 서비스
 * @see #203 부가 기능 API
 */
import { apiClient } from './client';
import type {
  CancelWaiterCallResponse,
  CreateWaiterCallRequest,
  CreateWaiterCallResponse,
  WaiterCallResponse,
} from './types';

/**
 * #1431 G1 — 매장별 웨이터 호출 항목 (admin#1467 BE).
 *
 * 영업시간 무관, customer-facing (guest 가능). locale 별 label 자동 적용.
 */
export interface WaiterCallItem {
  /** 매장 설정 항목 ID. 기본 카탈로그 항목(#1761)에는 부재. */
  id?: number;
  /** 기본 카탈로그 키(#1761). 매장 미설정 시 서버가 내려줌. id 와 배타. */
  default_key?: string;
  emoji: string | null;
  label: string;
}

export interface WaiterCallItemListResponse {
  data: WaiterCallItem[];
}

/**
 * #1432 G1 cross-repo — 매장 WiFi 정보 (admin#1480 BE).
 *
 * 테이블 세션 인증된 손님만 접근. apiClient 가 X-Session-Token 자동 부착.
 * - 미설정 매장: 404 → caller 에서 catch 하여 wifi 미노출
 * - 설정된 경우: { ssid, password } 평문 (BE 가 encrypted cast 자동 복호화)
 */
export interface StoreWifiInfo {
  ssid: string;
  password: string;
}

export interface StoreWifiResponse {
  data: StoreWifiInfo;
}

export const waiterCallApi = {
  /**
   * 웨이터 호출 요청
   * POST /api/v1/stores/:storeId/call-waiter
   *
   * @param storeId 매장 ID
   * @param data 호출 요청 데이터 (table_number 필수, message 선택)
   */
  callWaiter: (storeId: number, data: CreateWaiterCallRequest) =>
    apiClient.post<CreateWaiterCallResponse>(`/api/v1/stores/${storeId}/call-waiter`, data),

  /**
   * 웨이터 호출 상태 조회
   * GET /api/v1/stores/:storeId/waiter-calls/:callId
   *
   * @param storeId 매장 ID
   * @param callId 호출 ID
   */
  getCall: (storeId: number, callId: number) =>
    apiClient.get<WaiterCallResponse>(`/api/v1/stores/${storeId}/waiter-calls/${callId}`),

  /**
   * 웨이터 호출 취소
   * POST /api/v1/stores/:storeId/waiter-calls/:callId/cancel
   *
   * @param storeId 매장 ID
   * @param callId 호출 ID
   */
  cancelCall: (storeId: number, callId: number) =>
    apiClient.post<CancelWaiterCallResponse>(
      `/api/v1/stores/${storeId}/waiter-calls/${callId}/cancel`
    ),

  /**
   * #1431 G1 — 매장별 웨이터 호출 항목 조회.
   *
   * GET /api/v1/stores/:storeId/waiter-call-items?locale=...
   * 활성 항목만, locale 별 label 적용. 인증 불필요 (guest 가능).
   *
   * @param storeId 매장 ID
   * @param locale BCP47 locale (ko / es-MX / en 등)
   */
  listItems: (storeId: number, locale: string) =>
    apiClient.get<WaiterCallItemListResponse>(
      `/api/v1/stores/${storeId}/waiter-call-items?locale=${encodeURIComponent(locale)}`
    ),

  /**
   * #1432 G1 cross-repo — 매장 WiFi 정보 조회.
   *
   * GET /api/v1/stores/:storeId/wifi
   * X-Session-Token 자동 부착 (apiClient.buildHeaders).
   *
   * @param storeId 매장 ID
   */
  getWifi: (storeId: number) =>
    apiClient.get<StoreWifiResponse>(`/api/v1/stores/${storeId}/wifi`),
};
